import { type Content, type CMSProductShelfData } from '@/features/cms/components/CMS/types'
import { type ItemList, type Product } from 'schema-dts'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'

export const getProductShelfSchemas = ({
  canonicalUrl,
  city,
  bodyContent,
}: {
  canonicalUrl: string
  city: string | undefined
  bodyContent: Content[]
}): ItemList[] | undefined => {
  if (city) {
    const productShelves = bodyContent.filter(
      (item): item is CMSProductShelfData => item.content_type_id === 'product_shelf',
    )

    // Generate ItemList schemas for each product shelf
    return productShelves.reduce<ItemList[]>((itemListArray, productShelf) => {
      if (!productShelf.data.items) {
        return itemListArray
      }

      // Map each product in the shelf to a Product schema
      const itemListElement: Product[] = productShelf.data.items.map((product) => ({
        '@type': 'Product',
        name: product.display_name ?? '',
        image: product.image?.url ?? '',
        offers: {
          '@type': 'Offer',
          price: product.price?.toString() ?? '',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          // TODO: Once the PDP URL changes are in effect, we can update this to only return an ungated, universal product url https://app.clickup.com/t/86b7dfpkh
          ...(product.universal_id &&
            product.product_id &&
            product.display_name && {
              url: `https://www.shipt.com${getProductPageUrl({
                id: product.product_id,
                universal_id: product.universal_id,
                ungated: true,
              })}`,
            }),
        },
      }))

      // Create an ItemList schema for the product shelf
      if (itemListElement.length > 0) {
        const { id } = getCMSWrapperProps(productShelf)
        const itemList: ItemList = {
          '@type': 'ItemList',
          '@id': `${canonicalUrl}#${id}`,
          name: `Popular grocery products for ${city} delivery`,
          itemListElement,
        }

        itemListArray.push(itemList)
      }

      // Return the array of ItemList schemas
      return itemListArray
    }, [])
  }
}
